
import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/homepage/Navbar';
import Footer from '@/components/homepage/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ChevronRight, Download, ExternalLink } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { isAlternateDomain } from '@/utils/domainUtils';

interface TutorialLink {
  labelKey: string;
  url?: string;
  noteKey?: string;
}

interface Tutorial {
  id: string;
  titleKey: string;
  descriptionKey: string;
  videoUrl: string;
  links: TutorialLink[];
}

const TUTORIALS: Tutorial[] = [
  {
    id: 'radex-registration',
    titleKey: 'tutorials.radexRegistration.title',
    descriptionKey: 'tutorials.radexRegistration.description',
    videoUrl: 'https://www.youtube.com/embed/JIe-g7GI0U4?si=TIns3cNkNmetn-Ty',
    links: [
      {
        labelKey: 'tutorials.radexRegistration.links.registration',
        url: 'https://accopen.radexmarkets.com/?Ic=NSQEB&s=r6',
      },
    ],
  },
  {
    id: 'wire-transfer',
    titleKey: 'tutorials.wireTransfer.title',
    descriptionKey: 'tutorials.wireTransfer.description',
    videoUrl: 'https://www.youtube.com/embed/e8pkO6caG-0?si=OliQWCeJ2ouQuhZj',
    links: [
      {
        labelKey: 'tutorials.wireTransfer.links.login',
        url: 'https://my.radexmarkets.com/',
      },
    ],
  },
  {
    id: 'card-funding',
    titleKey: 'tutorials.cardFunding.title',
    descriptionKey: 'tutorials.cardFunding.description',
    videoUrl: 'https://www.youtube.com/embed/HiRNtRYnPXs?si=VL69Ow_znXnK_kT2',
    links: [
      {
        labelKey: 'tutorials.cardFunding.links.login',
        url: 'https://my.radexmarkets.com/',
      },
    ],
  },
  {
    id: 'pamm-registration',
    titleKey: 'tutorials.pammRegistration.title',
    descriptionKey: 'tutorials.pammRegistration.description',
    videoUrl: 'https://www.youtube.com/embed/QP50_MY61zQ?si=3hZ0W7gmnY08OJMI',
    links: [
      {
        labelKey: 'tutorials.pammRegistration.links.institutional',
        url: 'https://pamm.radexmarkets.com/app/join/74/gzcdqqcw',
      },
      {
        labelKey: 'tutorials.pammRegistration.links.spartan',
        url: 'https://pamm.radexmarkets.com/app/join/116/cptpxwso',
      },
      {
        labelKey: 'tutorials.pammRegistration.links.login',
        url: 'https://pamm.radexmarkets.com/app/auth/investor',
      },
    ],
  },
  {
    id: 'theclub-account',
    titleKey: 'tutorials.theclubAccount.title',
    descriptionKey: 'tutorials.theclubAccount.description',
    videoUrl: 'https://www.youtube.com/embed/bUrfxYDqxrM?si=tUqjNU_sN10yeAj7',
    links: [
      {
        labelKey: 'tutorials.theclubAccount.links.registration',
        noteKey: 'tutorials.theclubAccount.links.registrationNote',
      },
      {
        labelKey: 'tutorials.theclubAccount.links.website',
        url: 'https://theclub.finance/',
      },
    ],
  },
];

const Tutorials: React.FC = () => {
  const { t } = useLanguage();
  const [openTutorial, setOpenTutorial] = useState<string | null>('radex-registration');
  const isComingSoon = isAlternateDomain();


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTutorialClick = (tutorialId: string) => {
    setOpenTutorial(openTutorial === tutorialId ? null : tutorialId);
  };

  const handleDownloadPDF = () => {
    const pdfName = t('tutorials.common.pdfName');
    const link = document.createElement('a');
    link.href = `/pdfs/${pdfName}`;
    link.download = pdfName;
    link.click();
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full bg-purple-500/10 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl"></div>

        <div className="relative z-10">
          {/* Hero Section */}
          <section className="container mx-auto px-4 mt-20 pt-8 pb-4">
            <div className="text-center">
              <div className="hidden mb-8">
                <img
                  src="/logo_1_inverted_no_background.png"
                  alt="TheClub Logo"
                  className="h-20 w-auto mx-auto"
                />
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-center text-white mb-4">
                {t('tutorials.index.title')}
              </h1>
              <p className="text-lg text-gray-300 text-center max-w-3xl mx-auto mb-2">
                {t('tutorials.index.subtitle')}
              </p>
            </div>
          </section>

          {isComingSoon ? (
            <section className="container mx-auto px-4 pb-12">
              <div className="flex items-center justify-center py-20">
                <p className="text-4xl md:text-5xl font-bold text-white animate-pulse">
                  Coming Soon...
                </p>
              </div>
            </section>
          ) : (
            <section className="container mx-auto px-4 pb-12">
              <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
                {/* Left Sidebar - Tutorial List */}
                <aside className="lg:w-80 flex-shrink-0">
                  <div className="lg:sticky lg:top-28 space-y-2">
                    {TUTORIALS.map((tutorial) => (
                      <button
                        key={tutorial.id}
                        onClick={() => handleTutorialClick(tutorial.id)}
                        className={cn(
                          'w-full text-left px-5 py-3 rounded-lg transition-all duration-300 flex items-center justify-between group',
                          openTutorial === tutorial.id
                            ? 'bg-gradient-to-r from-purple-600/30 to-indigo-600/30 border-l-4 border-purple-500 shadow-lg'
                            : 'bg-gray-800/50 hover:bg-gray-800/70 border-l-4 border-transparent hover:border-purple-400/50'
                        )}
                      >
                        <span className={cn(
                          'font-medium text-sm leading-snug transition-colors',
                          openTutorial === tutorial.id
                            ? 'text-white'
                            : 'text-gray-300 group-hover:text-white'
                        )}>
                          {t(tutorial.titleKey)}
                        </span>
                        <ChevronRight
                          className={cn(
                            'w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-300',
                            openTutorial === tutorial.id
                              ? 'rotate-90 text-purple-400'
                              : 'text-gray-500 group-hover:text-purple-400'
                          )}
                        />
                      </button>
                    ))}
                  </div>
                </aside>

                {/* Right Content Area - Tutorial Details */}
                <div className="flex-1 space-y-2">
                  {TUTORIALS.map((tutorial) => (
                    <Collapsible
                      key={tutorial.id}
                      open={openTutorial === tutorial.id}
                      onOpenChange={() => handleTutorialClick(tutorial.id)}
                    >
                      <CollapsibleContent className="animate-accordion-down">
                        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-5 border border-white/10 space-y-4">
                          {/* Title and Description */}
                          <div>
                            <h2 className="text-2xl md:text-2xl font-bold text-white mb-2">
                              {t(tutorial.titleKey)}
                            </h2>
                            <p className="text-base text-gray-300 leading-relaxed">
                              {t(tutorial.descriptionKey)}
                            </p>
                          </div>

                          {/* Links Section */}
                          {tutorial.links.length > 0 && (
                            <div className="flex flex-col gap-2 pt-1">
                              {tutorial.links.map((link) => (
                                <div key={link.labelKey}>
                                  {link.url ? (
                                    <div className="flex items-center gap-2 text-sm">
                                      <ExternalLink className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                                      <a
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors"
                                      >
                                        {t(link.labelKey)}
                                      </a>
                                    </div>
                                  ) : (
                                     <div className="flex items-center gap-2 text-sm">
                                       <span className="text-gray-300">
                                         {t(link.labelKey)}:
                                       </span>
                                       {link.noteKey && (
                                         <span className="text-purple-400 italic">
                                           {t(link.noteKey)}
                                         </span>
                                       )}
                                     </div>
                                   )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* YouTube Video Embed */}
                          <div className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: '48.00%' }}>
                            <iframe
                              className="absolute top-0 left-0 w-full h-full rounded-lg"
                              src={tutorial.videoUrl}
                              title={t(tutorial.titleKey)}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              referrerPolicy="strict-origin-when-cross-origin"
                              allowFullScreen
                            />
                          </div>

                          {/* PDF Download */}
                          <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-lg p-3 border border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <p className="text-base font-semibold text-white">
                              {t('tutorials.common.pdfAlternative')}
                            </p>
                            <Button
                              onClick={() => handleDownloadPDF()}
                              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-all duration-200 hover:scale-105 whitespace-nowrap flex-shrink-0"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              {t('tutorials.common.downloadPdf')}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Tutorials;
